import React, { useState, useEffect } from "react";
import qs from "query-string";
import { useSelector } from "react-redux";
import { object, string, number, array, boolean } from "yup";
import { useIntl, FormattedMessage } from "react-intl";
import { useHistory, useParams, withRouter } from "react-router-dom";
import { Row, Card, Form, Space, Spinner } from "@masterteam/mt-design-react";

import api from "../../providers/api";
import { addToast } from "../../helpers";

import Steps from "../Steps";
import FormHeader from "../FormHeader";
import Header from "../KPIChangeDetails/Header";
import History from "../KPIChangeDetails/History";
import ConfirmationModal from "../ConfirmationModal";
import ChangeActualValues from "./ChangeActualValues";
import RequestDetails from "../KPIChangeDetails/RequestDetails";

import "./style.scss";

const validationSchema = (isSemiAuto) => {
  const shape = {
    Value: number()
      .nullable()
      .when(["IsSkipped"], {
        is: (isSkipped) => !isSkipped,
        then: number().required(),
      }),
    IsSkipped: boolean(),
    Target: number().nullable(),
    Attachments: array()
      .of(object())
      .when(["IsSkipped"], {
        is: (isSkipped) => !isSkipped,
        then: array().of(object()).min(1, " "),
        otherwise: array().of(object()),
      }),
    MoreInformation: string()
      .nullable()
      .when(["IsSkipped"], {
        is: (isSkipped) => !isSkipped,
        then: string().required(),
      }),
    Justification: string()
      .nullable()
      .when(["IsMandatory", "IsSkipped"], {
        is: (isMandatory, isSkipped) => isMandatory && !isSkipped,
        then: (schema) => schema.required(" "),
      }),
    CorrectiveAction: string()
      .nullable()
      .when(["IsMandatory", "IsSkipped"], {
        is: (isMandatory, isSkipped) => isMandatory && !isSkipped,
        then: (schema) => schema.required(" "),
      }),
  };

  if (isSemiAuto) {
    shape.Parameters = array().of(
      object().shape({
        Value: number().required(),
      })
    );
  }
  return object().shape({
    RequestDescription: string().required(" "),

    KPIMeasures: array().of(object().shape(shape)),
  });
};

const KPIChangeActualValues = ({ location }) => {
  const intl = useIntl();
  const { kpi, type } = qs.parse(location.search);
  const { id } = useParams();
  const history = useHistory();
  const [kpiDetails, setKpiDetails] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [kpiDetailsLoading, setKpiDetailsLoading] = useState(false);
  const [requestIsLoading, setRequestIsLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [user] = useSelector(({ user }) => [user.user]);

  const [initialValues, setInitialValues] = useState({
    ChangeType: type,
    CreatedBy: user.DisplayName,
    Created: new Date(),
    RequestDescription: "",
    KPIMeasures: [],
  });

  const getData = async () => {
    setIsLoading(true);
    try {
      if (id) {
        await getRequestData();
      }

      setIsLoading(false);
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getKpiDetails = async (id) => {
    setKpiDetailsLoading(true);
    let kpiRes;
    let measuresRes;
    try {
      kpiRes = await api.kpi.getById(id);
      measuresRes = await api.kpi.getChangeRequestMeasures(id);

      if (
        kpiRes?.StatusCode === "Success" &&
        measuresRes?.StatusCode === "Success"
      ) {
        const isSemiAuto =
          kpiRes?.Data.DataSource?.toLowerCase() === "semi-auto";

        setInitialValues({
          ...initialValues,
          KPIMeasures: measuresRes?.Data?.map((item) => ({
            ...item,
            Value: item?.Value === null ? "" : item?.Value,
            MeasureID: item.ID,
            Parameters: isSemiAuto
              ? item.Parameters.map((e) => {
                  return { ...e, Value: e?.Value ?? null };
                })
              : undefined,
          })),
        });
        setKpiDetails({
          ...kpiRes.Data,
          KPIMeasures: measuresRes?.Data?.map((item) => ({
            ...item,
            Value: item?.Value === null ? "" : item?.Value,
            MeasureID: item.ID,
            Parameters: isSemiAuto
              ? item.Parameters.map((e) => {
                  return { ...e, Value: e?.Value ?? null };
                })
              : undefined,
          })),
        });
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error",
      });
    } finally {
      setKpiDetailsLoading(false);
    }

    if (kpiRes?.StatusCode === "Success") {
      return kpiRes.Data;
    }
  };

  const getRequestData = async () => {
    setRequestIsLoading(true);
    try {
      const res = await api.workflow.getRequestByID(id);
      const Form = { ...res?.Data?.Form };

      setRequestData(res?.Data);
      setIsReadOnly(res?.Data?.Status !== 7);
      setKpiDetails(Form?.OldKPIDetails);

      setInitialValues({
        ...initialValues,
        RequestDescription: Form?.RequestDescription,
        ChangeType: type ?? Form?.ChangeType,
        KPIMeasures: Form?.KPIDetails.KPIMeasures,
        CreatedBy: res?.Data?.RequestedBy?.DisplayName,
      });
    } catch (err) {
      addToast.error(err.message, {
        toastId: "errorFetchingData",
      });
    } finally {
      setRequestIsLoading(false);
    }
  };

  const getAttachment = async (item) => {
    const oldAttachments = [];
    const newAttachments = [];
    item.Attachments?.forEach((item) =>
      item.ID ? oldAttachments.push(item) : newAttachments.push(item)
    );

    const { Data: attachments } = newAttachments?.length
      ? await api.attachment.create(newAttachments)
      : { Data: [] };
    if (!attachments) throw new Error("upload failed!");

    return {
      ...item,
      Attachments: [
        ...oldAttachments,
        ...attachments.map((e) => {
          return { ID: e };
        }),
      ],
    };
  };

  const onSubmit = async (values, isDraft = false) => {
    setIsSubmitting(true);
    try {
      let withAttachment = [];
      const withoutAttachment = [];

      values.KPIMeasures.forEach((item) => {
        if (item.Attachments?.length) {
          withAttachment.push(item);
        } else {
          withoutAttachment.push(item);
        }
      });

      let newListWithAttachment = withAttachment;
      if (withAttachment?.length) {
        newListWithAttachment = await Promise.all(
          withAttachment.map(getAttachment)
        );
      }

      const measuresList = [...newListWithAttachment, ...withoutAttachment];

      const body = {
        RequestID: id || 0,
        KPIID: kpiDetails?.ID,
        RequestDescription: values?.RequestDescription,
        ChangeType: values?.ChangeType,
        IsDraft: isDraft,
        KPIDetails: {
          ...values,
          KPIMeasures: measuresList,
        },
        OldKPIDetails: { ...kpiDetails },
      };

      const res = await api.kpi.changeKPIRequest(body);

      const { StatusCode } = res;

      if (StatusCode === "Success") {
        addToast.success("toast.sendOperation");
        history.push(`/splus/kpi-details/${kpiDetails?.ID}`);
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  async function fetchData() {
    getData();
    if (kpi) getKpiDetails(kpi);
  }

  useEffect(() => {
    fetchData();
  }, []);

  if (isLoading || kpiDetailsLoading || requestIsLoading)
    return (
      <Row justify="center" style={{ marginTop: "60px" }}>
        <Spinner size="large" />
      </Row>
    );

  return (
    <>
      <Header kpiDetails={kpiDetails} />
      <Card className="kpi-change-request">
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema(
            kpiDetails?.DataSource?.toLowerCase() === "semi-auto"
          )}
          onSubmit={() => {
            setIsConfirmationModalOpen(true);
          }}
        >
          {({ values, setFieldValue, errors }) => (
            <>
              <FormHeader
                canApprove={requestData?.CanApprove}
                isReadOnly={isReadOnly}
                getData={() => {
                  getRequestData();
                }}
                data={requestData}
                title="change-request-form"
                cancelRoute={`/splus/kpi-details/${kpiDetails?.ID}`}
                handleSaveAsDraft={() => {
                  onSubmit(values, true);
                }}
              />

              {isReadOnly ? <Steps steps={requestData?.Steps} /> : null}

              <Space className="form-body" direction="vertical" size="large">
                <Space className="request-type-wrapper">
                  <div className="label">
                    <FormattedMessage id="change-type" /> :
                  </div>
                  <div className="request-type">
                    <FormattedMessage id="change-kpi-actual-values" />
                  </div>
                </Space>

                <RequestDetails
                  kpiDetails={kpiDetails}
                  isReadOnly={isReadOnly}
                  errors={errors}
                />

                <ChangeActualValues
                  kpiDetails={kpiDetails}
                  isReadOnly={isReadOnly}
                  errors={errors}
                  values={values}
                  setFieldValue={setFieldValue}
                />

                {isReadOnly ? <History data={requestData?.WFHistory} /> : null}
              </Space>

              {isConfirmationModalOpen && (
                <ConfirmationModal
                  isOpen={isConfirmationModalOpen}
                  title={intl.formatMessage({ id: "kpi-change-request" })}
                  closeModal={() => {
                    setIsConfirmationModalOpen(false);
                    addToast.error("toast.cancelOperation");
                  }}
                  handleSubmit={() => {
                    onSubmit(values, false);
                  }}
                  ButtonStatus={isSubmitting}
                />
              )}
            </>
          )}
        </Form>
      </Card>
    </>
  );
};

export default withRouter(KPIChangeActualValues);
